import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Button, Container, Group, Title } from '@mantine/core';
import { useQuery } from 'react-query';
import { useIntlLight } from '../../../i18n';
import useAuth from '../../../../hooks/useAuth';
import useGlobalModal from '../../../ui-atoms/hooks/useGlobalModal';
import {
  getRequestAccessConfirmed,
  setRequestAccessConfirmed,
} from '../../../../services/LocalStorageService';
import { EpqAccessRequest } from '../../../api-client';
import { getEntities } from '../../../../services/request/assets';
import { ROUTE } from '../../../../types';

const RegisterRequestAccessContainer = () => {
  const { t } = useIntlLight();
  const navigate = useNavigate();
  const { open, close } = useGlobalModal();
  const { user, isRoleProducer } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const { data, isLoading } = useQuery([ROUTE.epqAccessRequest.queryKey, user?.id], () =>
    getEntities<EpqAccessRequest>(`${ROUTE.epqAccessRequest.api}?pagination=false`)
  );

  const handleConfirm = useCallback(() => {
    setShowModal(false);
    close?.();
    if (isRoleProducer) {
      setRequestAccessConfirmed(true);
      navigate(`/producer/${ROUTE.epqAccessRequest.formPath}`);
    }
  }, [close, isRoleProducer, navigate]);

  const handleConfirmLink = useCallback(() => {
    setShowModal(false);
    close?.();
    if (isRoleProducer) {
      setRequestAccessConfirmed(true);
      navigate('/producer/request_access', {
        state: {
          openedLink: true,
        },
      });
    }
  }, [close, isRoleProducer, navigate]);

  useEffect(() => {
    if (
      user &&
      user?.acceptedUseTerm &&
      isRoleProducer &&
      !getRequestAccessConfirmed() &&
      !isLoading
    ) {
      const hasRequestAccess = data?.find((requestAccess: EpqAccessRequest) =>
        ['SENT', 'IN_PROGRESS', 'ACCEPTED'].includes(requestAccess.requestStatus)
      );
      setShowModal(!hasRequestAccess);
    }
  }, [user, isRoleProducer, isLoading, data]);

  useEffect(() => {
    if (showModal) {
      open?.(
        <Container size="sm">
          <Title size={20} mb={24} align="center">
            {t('epqAccessRequest.modal.registerRequest.title')}
          </Title>
          <Group mb={24} position="center">
            <FormattedMessage id="epqAccessRequest.modal.registerRequest.text1" />
          </Group>
          <Group position="center">
            <Button onClick={handleConfirm}>
              {t('epqAccessRequest.modal.registerRequest.register')}
            </Button>
          </Group>
          <Group mb={24} mt={24} position="center">
            <FormattedMessage id="epqAccessRequest.modal.registerRequest.text2" />
          </Group>
          <Group position="center">
            <Button onClick={handleConfirmLink}>
              {t('epqAccessRequest.modal.registerRequest.link')}
            </Button>
          </Group>
        </Container>,
        {
          onClose: () => close?.(),
          preventDefaultClosing: true,
          size: 'auto',
        }
      );
    }
  }, [open, close, showModal, navigate, t, handleConfirm, handleConfirmLink]);

  return null;
};

export default RegisterRequestAccessContainer;
